/**
 * Module Vuex pour les réservations
 * Gère l'état des réservations et billets
 */
import { reservationService } from '@/services';

const state = {
  reservations: [],
  currentReservation: null,
  loading: false,
  error: null,
};

const getters = {
  allReservations: (state) => state.reservations,
  currentReservation: (state) => state.currentReservation,
  isLoading: (state) => state.loading,
  totalReservations: (state) => state.reservations.length,
  totalAmount: (state) => {
    return state.reservations.reduce((sum, res) => sum + (res.montantTotalReservation || 0), 0);
  },
  reservedSeats: (state) => {
    return state.reservations.reduce((sum, res) => sum + (res.nombrePlace || 0), 0);
  },
};

const mutations = {
  SET_RESERVATIONS(state, reservations) {
    state.reservations = reservations;
  },
  SET_CURRENT_RESERVATION(state, reservation) {
    state.currentReservation = reservation;
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  ADD_RESERVATION(state, reservation) {
    state.reservations.push(reservation);
  },
  UPDATE_RESERVATION(state, updatedReservation) {
    const index = state.reservations.findIndex(r => r.id === updatedReservation.id);
    if (index !== -1) {
      state.reservations.splice(index, 1, updatedReservation);
    }
  },
  REMOVE_RESERVATION(state, reservationId) {
    state.reservations = state.reservations.filter(r => r.id !== reservationId);
  },
};

const actions = {
  /**
   * Récupérer les réservations par vendeur
   */
  async fetchReservationsBySeller({ commit }, data = {}) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const reservations = await reservationService.getReservationsBySeller(data);
      commit('SET_RESERVATIONS', reservations);
      return reservations;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Récupérer les réservations par admin transport
   */
  async fetchReservationsByAdminTP({ commit }, data = {}) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const reservations = await reservationService.getReservationsByAdminTP(data);
      commit('SET_RESERVATIONS', reservations);
      return reservations;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Créer une réservation
   */
  async createReservation({ commit }, reservationData) {
    commit('SET_LOADING', true);
    try {
      const reservation = await reservationService.createReservation(reservationData);
      commit('ADD_RESERVATION', reservation);
      return reservation;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Générer un billet
   */
  async generateTicket({ commit }, ticketReference) {
    try {
      const ticket = await reservationService.generateTicket(ticketReference);
      return ticket;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    }
  },

  /**
   * Vérifier un billet
   */
  async verifyTicket({ commit }, ticketReference) {
    try {
      const result = await reservationService.verifyTicket(ticketReference);
      return result;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    }
  },

  /**
   * Confirmer un billet
   */
  async confirmTicket({ commit }, ticketReference) {
    try {
      const result = await reservationService.confirmTicket(ticketReference);
      return result;
    } catch (error) {
      commit('SET_ERROR', error);
      throw error;
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
